// Persistent storage for the device key and known GBRKs.
//
// WHAT THIS DOES AND DOES NOT PROTECT AGAINST
//
// It does NOT survive the user clearing site data. Nothing does. "Clear
// browsing data", Safari's "Remove All Website Data", or clearing storage for
// this origin in devtools deletes localStorage, IndexedDB and the Cache API
// unconditionally, and no web API opts out of that. Any design that needs to
// survive it has to keep the material somewhere other than this origin - see
// RECOVERY below.
//
// What it DOES protect against is eviction, which is the far more common way
// this data disappears and the one users never asked for:
//
//   - Safari/WebKit clears all script-writable storage for a site after 7 days
//     of no user interaction. A Home Screen app opened fortnightly would find
//     itself unpaired with no explanation. Storage marked persistent is exempt.
//   - Chromium evicts whole origins under storage pressure, worst-origin-first,
//     with no warning. Persistent origins are skipped.
//   - Firefox prompts, then exempts.
//
// So this turns "your device silently unpaired itself" into "your device stays
// paired unless you deliberately wipe it", which is the achievable half.
//
// RECOVERY, for the half this cannot cover
//
// The device key is not the root of anything - it is a local convenience. A
// wiped device re-pairs by scanning the QR again, and the GBRK is re-derived
// through the ORK network rather than restored from a backup. That is the
// intended recovery path and it needs no export file, no cloud copy of key
// material, and no new place for a secret to leak from.

const ATTEMPTED_KEY = "tide_storage_persist_attempted";

function supported() {
  return typeof navigator !== "undefined"
    && !!navigator.storage
    && typeof navigator.storage.persist === "function";
}

/** Is this origin's storage already exempt from eviction? */
export async function isPersisted() {
  try {
    if (!navigator?.storage?.persisted) return false;
    return await navigator.storage.persisted();
  } catch {
    return false;
  }
}

/**
 * Ask the browser to exempt this origin from eviction.
 *
 * Granting is at the browser's discretion and the heuristics differ: Chromium
 * grants silently for an installed app or a site with enough engagement,
 * Firefox prompts, Safari decides on its own. All of them are more willing once
 * the app is installed - which this app already requires - so this is called
 * after installation rather than on a first anonymous visit, where it would
 * more likely be refused and, in Firefox, spend a permission prompt to do it.
 *
 * Never throws. Failure here costs eviction resistance, not function.
 *
 * @returns {Promise<{supported:boolean, persisted:boolean, alreadyGranted:boolean}>}
 */
export async function ensurePersisted() {
  if (!supported()) {
    return { supported: false, persisted: false, alreadyGranted: false };
  }

  if (await isPersisted()) {
    return { supported: true, persisted: true, alreadyGranted: true };
  }

  try {
    const granted = await navigator.storage.persist();
    // Remembered only to keep the console quiet on repeat visits; the call
    // itself is safe to repeat, and a browser that refused once may grant later
    // as engagement builds, so this never blocks a retry.
    try { localStorage.setItem(ATTEMPTED_KEY, granted ? "granted" : "refused"); } catch { }

    if (!granted) {
      console.info(
        "[storage] the browser declined persistent storage; this device may be "
        + "unpaired if the app goes unused long enough for storage to be evicted"
      );
    }
    return { supported: true, persisted: granted, alreadyGranted: false };
  } catch {
    return { supported: true, persisted: false, alreadyGranted: false };
  }
}

/**
 * How much room the origin has, for diagnosing an eviction after the fact.
 * Both numbers are deliberately coarse in every browser that reports them.
 */
export async function usage() {
  try {
    if (!navigator?.storage?.estimate) return null;
    const { usage: used, quota } = await navigator.storage.estimate();
    return { used, quota };
  } catch {
    return null;
  }
}
